import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Sparkles, RefreshCw } from "lucide-react";

/**
 * "O que perdi?" — AI catch-up: summarizes the messages the user missed in
 * this conversation since their last read (or the recent conversation when
 * already caught up).
 */
export function CatchUpDialog({ open, onClose, conversationId, since }) {
  const { user, t } = useApp();
  const [data, setData] = useState(null); // {summary, count, caught_up}
  const [error, setError] = useState(false);

  const load = () => {
    setData(null);
    setError(false);
    api.post(`/conversations/${conversationId}/catchup`, { user_id: user.id, since: since || null })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationId]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md z-[260]" data-testid="catchup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> {t("catchup_title")}
          </DialogTitle>
        </DialogHeader>
        {!data && !error && (
          <div className="space-y-2 py-1" data-testid="catchup-loading">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        )}
        {error && (
          <div className="text-center py-4 space-y-3">
            <p className="text-sm text-destructive">{t("catchup_error")}</p>
            <Button variant="outline" size="sm" onClick={load} data-testid="catchup-retry">
              <RefreshCw className="h-4 w-4 mr-1.5" /> {t("retry")}
            </Button>
          </div>
        )}
        {data && !data.summary && (
          <p className="text-sm text-muted-foreground text-center py-6">{t("catchup_empty")}</p>
        )}
        {data && data.summary && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              {data.caught_up
                ? t("catchup_recent")
                : t("catchup_missed").replace("{n}", String(data.count))}
            </p>
            <div
              className="text-sm leading-relaxed whitespace-pre-wrap rounded-xl border bg-muted/30 p-3.5 max-h-[50vh] overflow-y-auto"
              data-testid="catchup-summary"
            >
              {data.summary}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
